import { directSupabase } from '../../../lib/supabaseClient';
import type { DocumentKind, DocumentStatus } from '../types/documents.types';

export type UnifiedSource = 'jarvis';

export interface UnifiedDoc {
  source: UnifiedSource;
  kind: DocumentKind;
  lifecycleRef?: string;
  refNo: string; // invoice_ref for invoices, lifecycle_ref otherwise
  title: string;
  customerName?: string;
  status?: string | DocumentStatus;
  total?: number;
  createdAt?: string;
  issuedAt?: string;
  documentId?: string; // JARVIS id
  projectId?: string | null;
}

export interface UnifiedFilters {
  source?: UnifiedSource | 'all';
  kind?: DocumentKind | 'all' | 'quote_estimate';
  status?: string | 'all';
  organisationId?: string;
  search?: string;
  createdAfter?: string;
  createdBefore?: string;
  inputVatSelected?: boolean;
  invoiceState?: string | null;
  pipelineState?: string | null;
  page?: number;
  pageSize?: number;
}

export interface UnifiedDocumentsResult {
  items: UnifiedDoc[];
  total: number;
  page: number;
  pageSize: number;
}

const SEARCHABLE_STATUSES: DocumentStatus[] = [
  'draft',
  'sent',
  'accepted',
  'rejected',
  'invoiced',
  'paid',
  'cash',
  'void',
];

const SEARCHABLE_KINDS: DocumentKind[] = ['Estimate', 'Quotation', 'Invoice', 'CISCertificate'];

const DEFAULT_PAGE_SIZE = 20;
const INVOICE_REF_PAGE_SIZE = 1000;

function sanitizeSearchTerm(raw?: string): string {
  return String(raw || '')
    .trim()
    .replace(/[%_,()"]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function quotePostgrestList(values: string[]): string {
  return `(${values.map((value) => `"${value.replace(/"/g, '')}"`).join(',')})`;
}

function buildSearchOrFilter(search: string): string {
  const needle = search.toLowerCase();
  const tokens = search.split(' ').filter(Boolean);
  const clauses = [
    `lifecycle_ref.ilike.%${search}%`,
    `invoice_ref.ilike.%${search}%`,
    `title.ilike.%${search}%`,
    `customer_first_name.ilike.%${search}%`,
    `customer_last_name.ilike.%${search}%`,
  ];

  if (tokens.length >= 2) {
    const firstName = tokens[0];
    const lastName = tokens.slice(1).join(' ');
    clauses.push(`and(customer_first_name.ilike.%${firstName}%,customer_last_name.ilike.%${lastName}%)`);
  }

  SEARCHABLE_STATUSES.filter((status) => status.includes(needle)).forEach((status) => {
    clauses.push(`status.eq.${status}`);
  });
  SEARCHABLE_KINDS.filter((kind) => kind.toLowerCase().includes(needle)).forEach((kind) => {
    clauses.push(`kind.eq.${kind}`);
  });

  return clauses.join(',');
}

function mapDocumentRow(row: any, titleOverride?: string): UnifiedDoc {
  const customerName = `${row.customer_first_name || ''} ${row.customer_last_name || ''}`.trim() || undefined;
  return {
    source: 'jarvis',
    kind: row.kind as DocumentKind,
    lifecycleRef: row.lifecycle_ref,
    refNo: row.kind === 'Invoice' && row.invoice_ref ? row.invoice_ref : row.lifecycle_ref,
    title: titleOverride || row.title || '',
    customerName,
    status: row.status,
    total: row.gross_total != null ? Number(row.gross_total) : undefined,
    createdAt: row.created_at,
    issuedAt: row.issued_at || undefined,
    documentId: row.id,
    projectId: row.project_id || null,
  };
}

async function resolveListTitles(rows: any[]): Promise<Map<string, string>> {
  const titlesByDocId = new Map<string, string>();
  if (rows.length === 0) return titlesByDocId;

  const versionIds = new Set<string>();
  const invoiceQuoteVersionByDocId = new Map<string, string>();

  rows.forEach((row) => {
    const versionId = String(row.active_version_id || '').trim();
    if (row.kind !== 'Invoice' && versionId) versionIds.add(versionId);
  });

  const invoices = rows.filter((row) => row.kind === 'Invoice');
  if (invoices.length > 0) {
    const refs = Array.from(new Set(
      invoices.map((row) => String(row.lifecycle_ref || '').trim()).filter(Boolean)
    ));
    const orgIds = Array.from(new Set(
      invoices.map((row) => String(row.organisation_id || '').trim()).filter(Boolean)
    ));

    if (refs.length > 0) {
      let quoteQuery = directSupabase
        .from('documents')
        .select('organisation_id, lifecycle_ref, active_version_id')
        .eq('kind', 'Quotation')
        .in('lifecycle_ref', refs)
        .not('active_version_id', 'is', null);

      if (orgIds.length > 0) quoteQuery = quoteQuery.in('organisation_id', orgIds);

      const { data: quotes, error: quoteError } = await quoteQuery;
      if (quoteError) throw new Error(quoteError.message);

      const quoteVersionByKey = new Map<string, string>();
      (quotes || []).forEach((quote: any) => {
        const key = `${String(quote.organisation_id || '').trim()}:${String(quote.lifecycle_ref || '').trim()}`;
        const versionId = String(quote.active_version_id || '').trim();
        if (!key.endsWith(':') && versionId && !quoteVersionByKey.has(key)) {
          quoteVersionByKey.set(key, versionId);
        }
      });

      invoices.forEach((invoice) => {
        const key = `${String(invoice.organisation_id || '').trim()}:${String(invoice.lifecycle_ref || '').trim()}`;
        const versionId = quoteVersionByKey.get(key);
        if (versionId) {
          invoiceQuoteVersionByDocId.set(invoice.id, versionId);
          versionIds.add(versionId);
        }
      });
    }
  }

  if (versionIds.size === 0) return titlesByDocId;

  const { data: versions, error: versionError } = await directSupabase
    .from('document_versions')
    .select('id, title')
    .in('id', Array.from(versionIds));
  if (versionError) throw new Error(versionError.message);

  const titleByVersionId = new Map<string, string>();
  (versions || []).forEach((version: any) => {
    const title = String(version.title || '').trim();
    if (title) titleByVersionId.set(version.id, title);
  });

  rows.forEach((row) => {
    const versionId = row.kind === 'Invoice'
      ? invoiceQuoteVersionByDocId.get(row.id)
      : String(row.active_version_id || '').trim();
    const title = versionId ? titleByVersionId.get(versionId) : undefined;
    if (title) titlesByDocId.set(row.id, title);
  });

  return titlesByDocId;
}

async function listInvoiceLifecycleRefs(): Promise<string[]> {
  const refs = new Set<string>();
  let from = 0;

  while (true) {
    const { data, error } = await directSupabase
      .from('documents')
      .select('lifecycle_ref')
      .eq('kind', 'Invoice')
      .range(from, from + INVOICE_REF_PAGE_SIZE - 1);

    if (error) throw new Error(error.message);

    const rows = data || [];
    rows.forEach((row) => {
      const ref = String(row.lifecycle_ref || '').trim();
      if (ref) refs.add(ref);
    });

    if (rows.length < INVOICE_REF_PAGE_SIZE) break;
    from += INVOICE_REF_PAGE_SIZE;
  }

  return Array.from(refs);
}

export const adapterService = {
  async getUnifiedDocuments(filters: UnifiedFilters = {}): Promise<UnifiedDocumentsResult> {
    const sourceFilter = filters.source ?? 'all';
    const kindFilter = filters.kind ?? 'all';
    const statusFilter = filters.status ?? 'all';
    const search = sanitizeSearchTerm(filters.search);
    const pageSize = Math.max(1, filters.pageSize || DEFAULT_PAGE_SIZE);
    const page = Math.max(1, filters.page || 1);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    if (sourceFilter !== 'all' && sourceFilter !== 'jarvis') {
      return { items: [], total: 0, page, pageSize };
    }

    let query = directSupabase
      .from('documents')
      .select('id, kind, lifecycle_ref, invoice_ref, title, status, gross_total, customer_first_name, customer_last_name, created_at, issued_at, active_version_id, organisation_id, project_id', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.organisationId) {
      query = query.eq('organisation_id', filters.organisationId);
    }

    const awaitingFinal = filters.pipelineState === 'awaiting_final';
    const notInvoiced = filters.invoiceState === 'not_invoiced';

    if (awaitingFinal || notInvoiced || kindFilter === 'quote_estimate') {
      query = query.in('kind', ['Estimate', 'Quotation']);
    } else if (kindFilter !== 'all') {
      query = query.eq('kind', kindFilter);
    }

    if (awaitingFinal) {
      query = query.in('status', ['draft', 'sent']);
    } else if (statusFilter === 'unpaid') {
      query = query.in('status', ['sent', 'draft']);
    } else if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    } else {
      query = query.neq('status', 'void');
    }

    const dateFieldForRange = (() => {
      if (awaitingFinal) return 'created_at';
      const normalized = String(statusFilter || '').toLowerCase();
      if (normalized === 'paid' || normalized === 'cash') return 'paid_at';
      if (normalized === 'accepted') return 'accepted_at';
      if (normalized === 'rejected') return 'rejected_at';
      if (normalized === 'sent') return 'sent_at';
      if (normalized === 'draft') return 'draft_at';
      if (normalized === 'invoiced') return 'invoiced_at';
      if (normalized === 'void') return 'voided_at';
      return 'created_at';
    })();

    if (filters.createdAfter) query = query.gte(dateFieldForRange, filters.createdAfter);
    if (filters.createdBefore) query = query.lt(dateFieldForRange, filters.createdBefore);
    if (typeof filters.inputVatSelected === 'boolean') {
      query = query.eq('input_vat_selected', filters.inputVatSelected);
    }

    if (notInvoiced) {
      const invoiceRefs = await listInvoiceLifecycleRefs();
      if (invoiceRefs.length > 0) {
        query = query.not('lifecycle_ref', 'in', quotePostgrestList(invoiceRefs));
      }
    }

    if (search) {
      query = query.or(buildSearchOrFilter(search));
    }

    const { data, error, count } = await query.range(from, to);
    if (error) throw new Error(error.message);

    const rows = ((data || []) as any[]);
    const titlesByDocId = await resolveListTitles(rows);

    return {
      items: rows.map((row: any) => mapDocumentRow(row, titlesByDocId.get(row.id))),
      total: count || 0,
      page,
      pageSize,
    };
  },
};
